import React, { useState, useEffect, useRef, useMemo } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import WizardProgressBar from './components/WizardProgressBar';
import PromptStep from './components/PromptStep';
import CategoryStep from './components/CategoryStep';
import EquipmentStep from './components/EquipmentStep';
import ProblemStep from './components/ProblemStep';
import PictureStep from './components/PictureStep';
import ServiceStep from './components/ServiceStep';
import UrgencyStep from './components/UrgencyStep';
import ScheduleStep from './components/ScheduleStep';
import CustomerInfoStep from './components/CustomerInfoStep';
import PaymentStep from './components/PaymentStep';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { emailNotificationService } from '../../services/serviceZoneService';
import { useAuth } from '../../contexts/AuthContext';
import { customerPortalService } from '../../services/customerPortalService';
import { getPaymentStepLabel, isQuoteService } from './servicePricing';

const STORAGE_KEY = 'plumberpro_booking_draft';

const ServiceBookingWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  // Une étape précédente ouverte en plus de l'étape en cours (ne remplace pas currentStep)
  const [openedPastStep, setOpenedPastStep] = useState(null);
  const [bookingData, setBookingData] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [stepValidation, setStepValidation] = useState({});
  const [showRecoveryBanner, setShowRecoveryBanner] = useState(false);
  const [recoveredData, setRecoveredData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { user, profile } = useAuth();

  const mainContentRef = useRef(null);
  const currentStepRef = useRef(currentStep);
  const openedPastStepRef = useRef(openedPastStep);
  const bookingDataRef = useRef(bookingData);

  // Garder les refs synchronisées
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    openedPastStepRef.current = openedPastStep;
  }, [openedPastStep]);

  useEffect(() => {
    bookingDataRef.current = bookingData;
  }, [bookingData]);

  useEffect(() => {
    window.history.pushState({ wizardStep: currentStep }, '');

    const handlePopState = () => {
      const data = bookingDataRef.current;
      const past = openedPastStepRef.current;
      const step = currentStepRef.current;

      // Fermer d'abord l'étape précédente ouverte
      if (past !== null) {
        setOpenedPastStep(null);
        window.history.pushState({ wizardStep: step }, '');
        return;
      }

      if (step > 0) {
        const prevStep = step - 1;
        if (bookingDataRef.current?.fromAcceptedQuote && prevStep < 5) {
          window.history.pushState({ wizardStep: step }, '');
          return;
        }
        setOpenedPastStep(null);
        setCurrentStep(prevStep);
        saveToLocalStorage(data, prevStep);
        window.history.pushState({ wizardStep: prevStep }, '');
      } else {
        navigate('/');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);

  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const hoursSinceLastSave = (Date.now() - parsed?.timestamp) / (1000 * 60 * 60);
        if (hoursSinceLastSave < 24) {
          setRecoveredData(parsed);
          setShowRecoveryBanner(true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.error('Failed to parse saved booking data:', error);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    const resume = location.state?.resumeFromAcceptedQuote;
    if (!resume) return;

    const startStep = Number(location.state?.startStep);
    const draft = location.state?.bookingData || {};
    const step = Number.isFinite(startStep) && startStep >= 0 ? startStep : 5;

    setBookingData(draft);
    setCurrentStep(step);
    setOpenedPastStep(null);
    setShowRecoveryBanner(false);
    setRecoveredData(null);

    const validation = {};
    for (let i = 0; i < step; i += 1) {
      validation[i] = true;
    }
    setStepValidation(validation);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...draft,
        currentStep: step,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('Failed to save quote resume draft:', error);
    }

    navigate('/service-booking-wizard', { replace: true, state: {} });
  }, [location.state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (stepParam === null) return;

    const stepNumber = parseInt(stepParam, 10);
    if (Number.isNaN(stepNumber) || stepNumber < 0) return;

    setCurrentStep(stepNumber);
    setOpenedPastStep(null);

    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setBookingData(parsed);
      } catch (error) {
        console.error('Erreur restauration réservation:', error);
      }
    }

    navigate('/service-booking-wizard', { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    if (!user?.id) return;

    const applyProfilePrefill = async () => {
      try {
        const prefill = await customerPortalService.getBookingPrefillData(user, profile);
        if (!prefill) return;

        setBookingData((prev) => customerPortalService.mergeBookingPrefill(prev, prefill));
      } catch (err) {
        console.error('Erreur préremplissage profil client:', err);
      }
    };

    applyProfilePrefill();
  }, [user, profile]);

  const getHeaderUserName = () => {
    if (profile?.full_name) return profile.full_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    if (user?.email) return user.email.split('@')[0];
    return 'Invité';
  };

  const saveToLocalStorage = (data, step) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        ...data,
        currentStep: step,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('Failed to save booking data:', error);
    }
  };

  const clearLocalStorage = () => localStorage.removeItem(STORAGE_KEY);

  const handleBookingComplete = () => {
    clearLocalStorage();
    setShowRecoveryBanner(false);
    setRecoveredData(null);
  };

  const handleRecoverData = () => {
    if (recoveredData) {
      const step = recoveredData?.currentStep || 0;
      setBookingData(recoveredData);
      setCurrentStep(step);
      setOpenedPastStep(null);
      setShowRecoveryBanner(false);
    }
  };

  const handleDismissRecovery = () => {
    setShowRecoveryBanner(false);
    clearLocalStorage();
  };

  const steps = useMemo(() => {
    const allSteps = [
      { id: 'category', label: 'Emplacement', component: CategoryStep },
      { id: 'equipment', label: 'Équipement', component: EquipmentStep },
      { id: 'problem', label: 'Problème', component: ProblemStep },
      { id: 'service', label: 'Solution', component: ServiceStep },
      { id: 'details', label: 'Informations supplémentaires', component: PictureStep },
      { id: 'urgency', label: 'Priorité', component: UrgencyStep },
      { id: 'schedule', label: 'Créneau', component: ScheduleStep },
      { id: 'customerInfo', label: 'Coordonnées', component: CustomerInfoStep },
      {
        id: 'payment',
        label: getPaymentStepLabel(bookingData?.service),
        component: PaymentStep,
      },
    ];

    // Demande de devis : pas de priorité ni de créneau
    const skipSchedule =
      isQuoteService(bookingData?.service) && !bookingData?.fromAcceptedQuote;
    if (skipSchedule) {
      return allSteps.filter((step) => step.id !== 'urgency' && step.id !== 'schedule');
    }
    return allSteps;
  }, [bookingData?.service, bookingData?.fromAcceptedQuote]);

  const urgencyStepIndex = steps.findIndex((step) => step.id === 'urgency');
  const URGENCY_STEP_INDEX = urgencyStepIndex >= 0 ? urgencyStepIndex : 5;
  const isQuoteResume = Boolean(bookingData?.fromAcceptedQuote);
  const lockedBeforeStep = isQuoteResume ? URGENCY_STEP_INDEX : 0;

  // Si le parcours devis retire des étapes, recentrer l'index courant
  useEffect(() => {
    if (currentStep >= steps.length) {
      setCurrentStep(Math.max(0, steps.length - 1));
    }
  }, [steps.length, currentStep]);

  const clearDownstreamFrom = (data, fromStepIndex) => {
    const cleared = { ...data };
    if (fromStepIndex <= 1) delete cleared.equipment;
    if (fromStepIndex <= 2) {
      delete cleared.problemId;
      delete cleared.problemName;
    }
    if (fromStepIndex <= 3) delete cleared.service;
    if (fromStepIndex <= 4) {
      delete cleared.description;
      delete cleared.photos;
    }
    if (fromStepIndex <= 5) delete cleared.urgency;
    if (fromStepIndex <= 6) {
      delete cleared.schedule;
      delete cleared.technician;
    }
    return cleared;
  };

  const scrollToStep = (stepIndex) => {
    setTimeout(() => {
      const stepElement = document.getElementById(`step-${stepIndex}`);
      if (stepElement) {
        stepElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  const handleGoToStep = (stepIndex) => {
    if (stepIndex > currentStep || stepIndex < 0) return;
    if (stepIndex < lockedBeforeStep) return;

    // Revenir à l'étape en cours → ferme l'autre étape ouverte
    if (stepIndex === currentStep) {
      setOpenedPastStep(null);
      scrollToStep(currentStep);
      return;
    }

    // Une seule étape précédente ouverte à la fois (l'étape en cours reste ouverte)
    setOpenedPastStep(stepIndex);
    scrollToStep(stepIndex);
  };

  const handleStepUpdate = (data, stepIndex) => {
    let updatedData = { ...bookingData, ...data };
    let resetToStep = null;

    if (stepIndex === 0 && data?.category?.id && data.category.id !== bookingData?.category?.id) {
      updatedData = clearDownstreamFrom(updatedData, 1);
      resetToStep = 1;
    }
    if (stepIndex === 1 && data?.equipment?.id && data.equipment.id !== bookingData?.equipment?.id) {
      updatedData = clearDownstreamFrom(updatedData, 2);
      resetToStep = 2;
    }
    if (stepIndex === 2 && data?.problemId && data.problemId !== bookingData?.problemId) {
      updatedData = clearDownstreamFrom(updatedData, 3);
      resetToStep = 3;
    }
    if (stepIndex === 3 && data?.service?.id && data.service.id !== bookingData?.service?.id) {
      updatedData = clearDownstreamFrom(updatedData, 4);
      // Devis : nettoyer priorité / créneau éventuellement déjà saisis
      if (isQuoteService(data.service) && !updatedData.fromAcceptedQuote) {
        delete updatedData.urgency;
        delete updatedData.schedule;
        delete updatedData.timeSlot;
        delete updatedData.technician;
      }
      resetToStep = 4;
    }

    setBookingData(updatedData);
    bookingDataRef.current = updatedData;

    if (resetToStep !== null) {
      setCurrentStep(resetToStep);
      setOpenedPastStep(null);
      setStepValidation((prev) => {
        const updated = { ...prev, [stepIndex]: true };
        for (let i = resetToStep; i < steps.length; i++) delete updated[i];
        return updated;
      });
      saveToLocalStorage(updatedData, resetToStep);
      scrollToStep(resetToStep);
      return;
    }

    saveToLocalStorage(updatedData, currentStep);

    const isEditingPastStep = stepIndex < currentStep;
    if (isEditingPastStep) return;

    if (stepIndex === 0 && data?.service) {
      setCurrentStep(URGENCY_STEP_INDEX);
      setOpenedPastStep(null);
      saveToLocalStorage(updatedData, URGENCY_STEP_INDEX);
    }
  };

  const handleNext = (stepIndex, isValid = true, options) => {
    if (!isValid) return;

    if (options?.goToStep !== undefined) {
      handleGoToStep(options.goToStep);
      return;
    }

    const isEditingPastStep = stepIndex < currentStep;

    let nextStep = options?.skipToStep !== undefined
      ? options.skipToStep
      : (options?.skipToUrgency ? URGENCY_STEP_INDEX : stepIndex + 1);

    nextStep = Math.min(nextStep, steps.length - 1);

    setStepValidation((prev) => ({ ...prev, [stepIndex]: true }));

    if (isEditingPastStep) {
      // Remonter vers une étape déjà atteinte, sans fermer l'étape en cours
      if (nextStep >= currentStep) {
        setOpenedPastStep(null);
        scrollToStep(currentStep);
      } else {
        setOpenedPastStep(nextStep);
        scrollToStep(nextStep);
      }
      return;
    }

    setOpenedPastStep(null);
    setCurrentStep(nextStep);
    saveToLocalStorage(bookingDataRef.current, nextStep);
    window.history.pushState({ wizardStep: nextStep }, '');
    scrollToStep(nextStep);
  };

  const handleSkipToWizard = () => {
    setCurrentStep(1);
    setOpenedPastStep(null);
    saveToLocalStorage(bookingData, 1);
    window.history.pushState({ wizardStep: 1 }, '');
  };

  const handleBack = (stepIndex, isValid = true, options) => {
    if (!isValid) return;

    // Depuis l'étape en cours uniquement : revenir à la précédente et fermer l'étape en cours
    if (stepIndex !== currentStep) return;

    if (isQuoteResume && stepIndex <= lockedBeforeStep) {
      return;
    }

    const prevStep = options?.skipToStep !== undefined
      ? options.skipToStep
      : Math.max(stepIndex - 1, 0);

    if (isQuoteResume && prevStep < lockedBeforeStep) {
      return;
    }

    setOpenedPastStep(null);
    setCurrentStep(prevStep);
    saveToLocalStorage(bookingDataRef.current, prevStep);
    window.history.pushState({ wizardStep: prevStep }, '');
    scrollToStep(prevStep);
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    setSubmitError('');
    let appointmentResult = [];

    if (bookingData?.service?.checklists && Array.isArray(bookingData?.service?.checklists)) {
      appointmentResult.push({ checklists: bookingData?.service?.checklists, images: [] });
    }

    try {
      const appointmentData = {
        reference: `RDV-${Date.now()}`,
        customerName: bookingData?.address ? `Client ${bookingData?.city}` : 'Client',
        customerEmail: bookingData?.payment?.email || '',
        customerPhone: bookingData?.payment?.phone || '',
        serviceType: bookingData?.service?.name || 'Service',
        urgency: bookingData?.urgency?.level || 'normal',
        status: 'planned',
        scheduledDate: bookingData?.schedule?.date,
        scheduledTimeStart: bookingData?.schedule?.timeSlot,
        customerAddress: `${bookingData?.address}, ${bookingData?.postalCode} ${bookingData?.city}`,
        customerLatitude: 48.8566,
        customerLongitude: 2.3522,
        priceTotal: bookingData?.payment?.amount || 0,
        paymentStatus: 'succeeded',
        paymentMethod: 'card',
        appointmentResult,
        Service: bookingData?.service?.id,
      };

      const { data: appointment, error: appointmentError } = await supabase
        ?.from('appointments')
        ?.insert({
          reference: appointmentData?.reference,
          customer_name: appointmentData?.customerName,
          customer_email: appointmentData?.customerEmail,
          customer_phone: appointmentData?.customerPhone,
          service_type: appointmentData?.serviceType,
          urgency: appointmentData?.urgency,
          status: appointmentData?.status,
          scheduled_date: appointmentData?.scheduledDate,
          scheduled_time_start: appointmentData?.scheduledTimeStart,
          customer_address: appointmentData?.customerAddress,
          customer_latitude: appointmentData?.customerLatitude,
          customer_longitude: appointmentData?.customerLongitude,
          price_total: appointmentData?.priceTotal,
          payment_status: appointmentData?.paymentStatus,
          payment_method: appointmentData?.paymentMethod,
          appointment_result: appointmentData?.appointmentResult,
          Service: appointmentData?.Service,
        })
        ?.select()
        ?.single();

      if (appointmentError) throw appointmentError;

      try {
        await emailNotificationService?.sendBookingNotification(
          {
            reference: appointment?.reference,
            customerName: appointment?.customer_name,
            customerEmail: appointment?.customer_email,
            serviceType: appointment?.service_type,
            scheduledDate: appointment?.scheduled_date,
            scheduledTimeStart: appointment?.scheduled_time_start,
            customerAddress: appointment?.customer_address,
            priceTotal: appointment?.price_total,
          },
          'booking_confirmed'
        );
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      clearLocalStorage();
      setRecoveredData(null);
      setIsSubmitting(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Booking submission error:', error);
      setSubmitError(error?.message || 'Une erreur est survenue lors de la réservation');
      setIsSubmitting(false);
    }
  };

  const handleViewTracking = () => {
    navigate(
      isQuoteService(bookingData?.service) ? '/mes-devis' : '/public-service-tracking'
    );
  };

  const handleNewBooking = () => {
    setCurrentStep(0);
    setOpenedPastStep(null);
    setBookingData({});
    setIsCompleted(false);
    clearLocalStorage();
  };

  const handleLogout = () => navigate('/');

  const allStepsCompleted = () => {
    return steps.every((_, index) => stepValidation[index] === true);
  };

  if (isCompleted) {
    return (
      <>
        <RoleBasedHeader
          userRole={profile?.role || user?.user_metadata?.role || 'client'}
          userName={getHeaderUserName()}
          onLogout={handleLogout}
        />
        <div className="main-content bg-background min-h-screen" ref={mainContentRef}>
          <div className="max-w-3xl mx-auto px-4 lg:px-6 py-12">
            <div className="bg-card rounded-lg shadow-lg p-8 lg:p-12 text-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={48} color="var(--color-success)" />
              </div>

              <h1 className="text-3xl font-bold text-foreground mb-4">
                {isQuoteService(bookingData?.service)
                  ? 'Demande reçue'
                  : 'Réservation confirmée !'}
              </h1>

              <p className="text-lg text-muted-foreground mb-8">
                {isQuoteService(bookingData?.service)
                  ? (
                    <>
                      Votre demande de devis a bien été reçue.
                      <br />
                      Vous recevrez votre proposition par e-mail dans les plus brefs délais.
                    </>
                  )
                  : 'Votre intervention est planifiée. Vous recevrez un email de confirmation avec tous les détails.'}
              </p>

              <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold text-foreground mb-4">Détails de votre réservation</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="MapPin" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Adresse</p>
                      <p className="text-sm text-muted-foreground">
                        {bookingData?.address}, {bookingData?.postalCode} {bookingData?.city}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Icon name="Wrench" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Service</p>
                      <p className="text-sm text-muted-foreground">{bookingData?.service?.name}</p>
                    </div>
                  </div>

                  {!isQuoteService(bookingData?.service) && (
                  <div className="flex items-start gap-3">
                    <Icon name="Calendar" size={20} className="text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Date et heure</p>
                      <p className="text-sm text-muted-foreground">
                        {bookingData?.schedule?.date &&
                          new Date(bookingData.schedule.date)?.toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                      </p>
                      <p className="text-sm text-muted-foreground">{bookingData?.schedule?.timeSlot}</p>
                    </div>
                  </div>
                  )}

                  {bookingData?.technician && (
                    <div className="flex items-start gap-3">
                      <Icon name="User" size={20} className="text-primary mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Technicien</p>
                        <p className="text-sm text-muted-foreground">{bookingData?.technician?.name}</p>
                      </div>
                    </div>
                  )}

                  {bookingData?.payment?.amount > 0 && (
                    <div className="flex items-start gap-3 pt-3 border-t border-border">
                      <Icon name="CreditCard" size={20} className="text-primary mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Montant</p>
                        <p className="text-lg font-bold text-primary">
                          {bookingData?.payment?.amount?.toFixed(2)} € TTC
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 bg-gradient-to-r from-success/10 to-success/5 border-2 border-success/20 rounded-xl mb-6">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <p className="font-semibold text-foreground">Paiement pris en compte</p>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Votre paiement a bien été enregistré. Un reçu vous sera envoyé à{' '}
                  <span className="font-medium text-foreground">{bookingData?.payment?.email}</span>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="default" size="lg" iconName="MapPin" onClick={handleViewTracking}>
                  {isQuoteService(bookingData?.service)
                    ? 'Suivre mon devis'
                    : 'Suivre mon intervention'}
                </Button>
                <Button variant="outline" size="lg" iconName="Plus" onClick={handleNewBooking}>
                  Nouvelle réservation
                </Button>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Bell" size={16} />
                  <span>Vous recevrez des rappels la veille et 2h avant votre rendez-vous</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <RoleBasedHeader
        userRole={profile?.role || user?.user_metadata?.role || 'client'}
        userName={getHeaderUserName()}
        onLogout={handleLogout}
      />
      <div className="main-content bg-background min-h-screen">
        {showRecoveryBanner && (
          <div className="bg-warning/10 border-b-2 border-warning/20">
            <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Icon name="AlertCircle" size={24} color="var(--color-warning)" />
                  <div>
                    <p className="font-semibold text-foreground">Réservation en cours récupérée</p>
                    <p className="text-sm text-muted-foreground">
                      Nous avons retrouvé une réservation non terminée. Souhaitez-vous la reprendre ?
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleDismissRecovery}>Ignorer</Button>
                  <Button variant="default" size="sm" onClick={handleRecoverData}>Reprendre</Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="sticky top-16 z-[900] bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="max-w-5xl mx-auto px-4 lg:px-6 py-3">
            <WizardProgressBar currentStep={currentStep} steps={steps} />
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 lg:px-6 py-8 lg:py-12">
          <div className="bg-card rounded-2xl shadow-lg border border-border overflow-hidden">
            <div className="divide-y divide-border booking-wizard-steps [&_.max-w-5xl]:max-w-none [&_.max-w-6xl]:max-w-none [&_.bg-card.rounded-lg.shadow-md]:bg-transparent [&_.bg-card.rounded-lg.shadow-md]:shadow-none [&_.bg-card.rounded-lg.shadow-md]:border-0 [&_.bg-card.rounded-lg.shadow-md]:p-0">
            {steps.map((step, index) => {
              const StepComponent = step.component;

              if (index > currentStep) return null;

              const isCurrent = index === currentStep;
              const isPastOpen = openedPastStep === index;
              const isOpen = isCurrent || isPastOpen;
              const isPastClosed = index < currentStep && !isPastOpen;

              return (
                <section
                  key={step.id}
                  id={`step-${index}`}
                  className={`scroll-mt-28 ${isCurrent ? 'bg-primary/[0.02] min-h-[calc(100vh-11rem)]' : ''}`}
                >
                  {isOpen ? (
                    <div
                      className={`flex items-center gap-3 py-4 ${
                        step.id === 'equipment' ? 'px-3 sm:px-6 lg:px-8' : 'px-6 lg:px-8'
                      } ${
                        isCurrent
                          ? 'border-l-4 border-l-primary'
                          : 'border-l-4 border-l-primary/50'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground">{step.label}</h3>
                        {isPastOpen && (
                          <p className="text-xs text-primary">Modification</p>
                        )}
                      </div>
                      {isPastOpen && (
                        <button
                          type="button"
                          onClick={() => handleGoToStep(currentStep)}
                          className="text-xs text-primary hover:underline flex-shrink-0"
                        >
                          Revenir à l&apos;étape en cours
                        </button>
                      )}
                    </div>
                  ) : index < lockedBeforeStep ? (
                    <div
                      className={`w-full flex items-center gap-3 px-6 lg:px-8 py-3 text-left ${
                        isPastClosed
                          ? 'border-l-4 border-l-primary/40'
                          : 'border-l-4 border-l-border'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground">{step.label}</h3>
                        <p className="text-xs text-muted-foreground">Complété — non modifiable</p>
                      </div>
                      <Icon name="Lock" size={16} className="text-muted-foreground flex-shrink-0" />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleGoToStep(index)}
                      className={`w-full flex items-center gap-3 px-6 lg:px-8 py-3 text-left hover:bg-muted/40 ${
                        isPastClosed
                          ? 'border-l-4 border-l-primary/40'
                          : 'border-l-4 border-l-border'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground">{step.label}</h3>
                        <p className="text-xs text-primary">Complété — cliquer pour ouvrir</p>
                      </div>
                      <Icon name="ChevronDown" size={18} className="text-primary flex-shrink-0" />
                    </button>
                  )}

                  {isOpen && (
                    <div
                      className={`pb-6 lg:pb-8 ${
                        step.id === 'equipment'
                          ? 'px-2 sm:px-4 lg:px-6'
                          : 'px-4 lg:px-6'
                      }`}
                    >
                      <StepComponent
                        user={user}
                        profile={profile}
                        data={bookingData}
                        stepIndex={index}
                        currentStep={currentStep}
                        onUpdate={(data) => handleStepUpdate(data, index)}
                        onNext={(isValid, options) => handleNext(index, isValid, options)}
                        onBack={(isValid, options) => handleBack(index, isValid, options)}
                        onComplete={handleComplete}
                        onBookingComplete={handleBookingComplete}
                        onSkipToWizard={index === 0 && currentStep === 0 ? handleSkipToWizard : undefined}
                        isReadOnly={index < lockedBeforeStep}
                        isActive={isCurrent}
                        showBack={isCurrent && !(isQuoteResume && index <= lockedBeforeStep)}
                      />
                    </div>
                  )}
                </section>
              );
            })}
            </div>

            {currentStep === steps.length - 1 && allStepsCompleted() && (
              <div className="flex justify-center px-6 py-6 border-t border-border bg-muted/20">
                <Button
                  variant="default"
                  size="lg"
                  iconName="CheckCircle"
                  onClick={handleComplete}
                  disabled={isSubmitting}
                  className="min-w-[200px]"
                >
                  {isSubmitting ? 'Confirmation en cours...' : 'Confirmer la réservation'}
                </Button>
              </div>
            )}

            {submitError && (
              <div className="mx-6 mb-6 bg-error/10 border border-error/20 rounded-lg p-4 text-error">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Erreur lors de la réservation</p>
                    <p className="text-sm">{submitError}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSuccessModal(false)}>
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Réservation confirmée !</h3>
              <p className="text-muted-foreground mb-6">
                Votre réservation a été enregistrée avec succès. Vous recevrez un email de confirmation.
              </p>
              <div className="flex flex-col gap-3">
                <Button variant="default" fullWidth onClick={() => {
                  setShowSuccessModal(false);
                  setIsCompleted(true);
                }}>
                  Voir le récapitulatif
                </Button>
                <Button variant="outline" fullWidth onClick={() => {
                  setShowSuccessModal(false);
                  handleNewBooking();
                }}>
                  Nouvelle réservation
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceBookingWizard;